import { useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

import { useUpdateRecipeTag } from '../api/recipe-tag-mutations';
import { RecipeTag } from '../types/recipe-tag-types';

interface EditRecipeTagDialogProps {
  tag: RecipeTag | null;
  onClose: () => void;
}

export function EditRecipeTagDialog({
  tag,
  onClose,
}: EditRecipeTagDialogProps) {
  const { tenantId } = useParams({
    from: '/_authenticated/$tenantId/settings/recipe-tags',
  });

  const [tagName, setTagName] = useState('');
  const updateTag = useUpdateRecipeTag();

  // Update local state when tag prop changes
  useEffect(() => {
    if (tag) {
      setTagName(tag.title || '');
    }
  }, [tag]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tag || !tagName.trim()) return;

    try {
      await updateTag.mutateAsync({
        id: tag.id,
        title: tagName.trim(),
        organisation_id: tenantId,
      });
      onClose();
      toast.success('Tag updated successfully');
    } catch {
      toast.error('Failed to update tag');
    }
  };

  const handleClose = () => {
    if (tag) {
      setTagName(tag.title || '');
    }
    onClose();
  };

  if (!tag) return null;

  return (
    <Dialog open={!!tag} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Recipe Tag</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="tagName" className="text-sm font-medium">
              Tag Name
            </label>
            <Input
              id="tagName"
              value={tagName}
              onChange={(e) => setTagName(e.target.value)}
              placeholder="Enter tag name (e.g., Breakfast, Vegetarian)"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!tagName.trim() || updateTag.isPending}
            >
              {updateTag.isPending ? 'Updating...' : 'Update Tag'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
